import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type {
  Cart,
  GiftCard,
  GiftCardReserveResponse,
} from "@server/gql/graphql";
import { addGiftCard } from "@server/requests/giftcard/addGiftCard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export type AddGiftcardInput = { giftCard: GiftCard; amount: number };

type AddGiftcard = <T extends boolean = false>(
  input: AddGiftcardInput,
  async?: T,
) => T extends true ? Promise<GiftCardReserveResponse | undefined> : void;

export default function useAddGiftcard() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (input: AddGiftcardInput) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return addGiftCard({
        ...input,
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);
      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const addGiftcard = useCallback(
    (input: AddGiftcardInput, async = false) => {
      if (async) return mutateAsync(input);
      return mutate(input);
    },
    [mutate, mutateAsync],
  );

  return {
    addGiftcard: addGiftcard as AddGiftcard,
    isErrorAddGiftcard: isError,
    isPendingAddGiftcard: isPending,
    isSuccessAddGiftcard: isSuccess,
  };
}
